import React from 'react';
import { SuperTooltipTemplate } from './types';
import {
  CTA,
  Image,
  InlineContent,
  StepIndicator,
  SuperTooltipTips,
} from './SuperTooltipParts';
import { Modal } from '@design-system/components/modal/modal';
import { ReadMore } from './components/ReadMore';
import * as styles from './SuperTooltipRenderer.css';
import { NavigationButton } from './components/NavigationButton';
import { faCaretLeft, faCaretRight } from '@fortawesome/pro-solid-svg-icons';
import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { resolveImagePath } from './utils';
import { SuperTooltipVM } from './super-tooltip.viewModel';
import { TagsCloud } from './components/Tags';

type TemplateContentProps = {
  template: SuperTooltipTemplate;
  vm: SuperTooltipVM;
};

const TemplateContent: React.FC<TemplateContentProps> = ({ template, vm }) => {
  switch (template.type) {
    case 'basic':
      return (
        <Col gap="l">
          <InlineContent>{template.content}</InlineContent>
          <TagsCloud tags={template.tags} />
          {template.imageUrl && (
            <Image src={template.imageUrl} alt={template.imageAlt || ''} />
          )}
          <CTA {...template} onClick={vm.onLinkClicked} />
          <SuperTooltipTips tips={template.tips} />
          <ReadMore {...template} onClick={vm.onLinkClicked} />
        </Col>
      );

    case 'single-image':
      if (template.layout === 'two-column') {
        return (
          <Col gap="l">
            <Row>
              <Col>
                <SuperTooltipTips tips={template.tips} />
              </Col>
              <Col center>
                <InlineContent>{template.content}</InlineContent>
                <Image src={template.imageUrl} alt={template.imageAlt} />
              </Col>
            </Row>
            <ReadMore {...template} onClick={vm.onLinkClicked} />
          </Col>
        );
      }
      return (
        <Col gap="l">
          <InlineContent>{template.content}</InlineContent>
          <Col center>
            <Image src={template.imageUrl} alt={template.imageAlt} />
          </Col>
          <CTA {...template} onClick={vm.onLinkClicked} />
          <SuperTooltipTips tips={template.tips} />
          <ReadMore {...template} onClick={vm.onLinkClicked} />
        </Col>
      );

    case 'two-image':
      return (
        <Col gap="l">
          <InlineContent>{template.content}</InlineContent>
          <Row
            style={{
              display: 'grid',
              gridTemplateColumns:
                template.layout === 'one-column' ? '1fr' : '1fr 1fr',
            }}
            gap="l"
            alignCenter
          >
            <Image src={template.imageUrls[0]} alt={template.imageAlts[0]} />
            <Image src={template.imageUrls[1]} alt={template.imageAlts[1]} />
          </Row>
          <SuperTooltipTips tips={template.tips} />
          <ReadMore {...template} onClick={vm.onLinkClicked} />
        </Col>
      );

    case 'video':
      return (
        <Col>
          <Row>
            <Col gap="l">
              <Typography
                variant="body"
                size="large"
                fontWeight="medium"
                style={{ whiteSpace: 'pre-line' }}
              >
                {template.content}
              </Typography>
              <SuperTooltipTips tips={template.tips} />
            </Col>
            <Col center>
              <video
                controls
                poster={resolveImagePath(template.videoThumbnailUrl)}
                preload="none"
                style={{ width: 'auto', maxHeight: '300px' }}
              >
                <source src={template.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </Col>
          </Row>
          <ReadMore {...template} onClick={vm.onLinkClicked} />
        </Col>
      );
  }
};

const SuperTooltipRenderer: React.FC<SuperTooltipVM> = (vm) => {
  const { templates, currentIndex, onGoToNext, onGoToPrev, ...p } = vm;
  const currentTemplate = templates[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < templates.length - 1;
  return (
    <Modal
      isOpen={p.isOpen}
      onClose={p.onClose}
      title={currentTemplate.title}
      modalClassName={styles.modal}
    >
      <Col className={styles.templateWrapper}>
        <Row
          className={
            templates.length > 1 ? styles.contentWithNav : styles.content
          }
        >
          {templates.length > 1 && (
            <NavigationButton
              icon={faCaretLeft}
              onClick={onGoToPrev}
              disabled={!hasPrev}
              aria-label="Next slide"
            />
          )}
          <TemplateContent template={currentTemplate} vm={vm} />

          {templates.length > 1 && (
            <NavigationButton
              icon={faCaretRight}
              onClick={onGoToNext}
              disabled={!hasNext}
              aria-label="Next slide"
            />
          )}
        </Row>
        <StepIndicator
          totalSteps={templates.length}
          currentStep={currentIndex}
          onClick={vm.onGoToSlide}
        />
      </Col>
    </Modal>
  );
};

export default SuperTooltipRenderer;
